import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DollarSign, Gift } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

const money = (v) => `$${(v ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AffiliateSettleDialog({ coupon, onClose, onDone }) {
  const pts = coupon.commission_unit === "points";
  const outstanding = coupon.commission_outstanding || 0;
  const [method, setMethod] = useState(coupon.affiliate_user_id ? "points" : "cash");
  const [amount, setAmount] = useState(String(outstanding));
  const [note, setNote] = useState("");
  const [redeemRate, setRedeemRate] = useState(100);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setRedeemRate(data.rewards_redeem_rate || 100)).catch(() => {});
  }, []);

  const amt = parseFloat(amount) || 0;
  const fmt = (v) => (pts ? `${Math.round(v).toLocaleString()} pts` : money(v));
  const pointsPreview = pts ? Math.round(amt) : Math.round(amt * redeemRate);
  const linked = !!coupon.affiliate_user_id;

  const submit = async () => {
    if (amt <= 0 || amt > outstanding + 1e-9) {
      toast.error(`Enter an amount between 0 and ${fmt(outstanding)}`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/coupons/${coupon.id}/payouts`, { amount: amt, method, note });
      toast.success(method === "points"
        ? `${data.points_credited.toLocaleString()} reward points credited to ${coupon.affiliate_user_email}`
        : `${fmt(amt)} recorded as paid in dollars`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payout");
    } finally {
      setSaving(false);
    }
  };

  const Option = ({ value, icon: Icon, title, desc, disabled, testid }) => (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={() => setMethod(value)}
      className={`flex items-start gap-3 text-left border rounded-lg p-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${method === value ? "border-[#8C7B6B] bg-[#F3EFE9] ring-1 ring-[#8C7B6B]/30" : "border-[#DDD7CF] hover:border-[#8C7B6B]"}`}
    >
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${method === value ? "text-[#8C7B6B]" : "text-[#9A9088]"}`} />
      <span>
        <span className="block text-sm font-semibold text-[#1A1A1A]">{title}</span>
        <span className="block text-xs text-[#6E675F] mt-0.5">{desc}</span>
      </span>
    </button>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="affiliate-settle-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-sm uppercase tracking-wide">Mark settled — {coupon.affiliate_name || coupon.code}</DialogTitle>
          <DialogDescription className="text-xs">
            <strong className="text-[#1A1A1A]" data-testid="settle-outstanding">{fmt(outstanding)}</strong> outstanding on <span className="font-mono">{coupon.code}</span>
            {linked ? <> · linked to <strong className="text-[#1A1A1A]">{coupon.affiliate_user_email}</strong></> : " · no customer account linked"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Option value="cash" icon={DollarSign} title="Dollars" desc="Paid outside the store (e-transfer, cash). Recorded only." testid="settle-method-cash" />
            <Option value="points" icon={Gift} title="Reward points" desc={linked ? "Credited to the linked account's balance instantly." : "Link a customer account first."} disabled={!linked} testid="settle-method-points" />
          </div>

          <div>
            <label className="text-xs text-[#6E675F] block mb-1.5">Amount to settle ({pts ? "points" : "CAD"})</label>
            <div className="flex gap-2">
              <Input data-testid="settle-amount" type="number" min="0" step={pts ? "1" : "0.01"} value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Button type="button" variant="outline" onClick={() => setAmount(String(outstanding))} data-testid="settle-amount-all">All</Button>
            </div>
            {method === "points" && amt > 0 && (
              <p className="text-xs text-[#2D6A4F] mt-2" data-testid="settle-points-preview">
                → <strong>{pointsPreview.toLocaleString()} reward points</strong> will be credited{pts ? "" : ` (${redeemRate} pts = $1)`}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-[#6E675F] block mb-1.5">Note (optional)</label>
            <Input data-testid="settle-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={method === "points" ? "e.g. Q2 commission" : "e.g. e-transfer sent June 12"} maxLength={300} />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="settle-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#2D6A4F] text-white hover:bg-[#245740]" data-testid="settle-confirm">
            {saving ? "Saving…" : method === "points" ? "Credit Points" : "Record Payout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
